import React, { useEffect, useState } from "react";

import EventService from "service/event/EventService";
import ReviewPrescription from "modules/prescription/components/ReviewPrescription";
import { CC_V2_REFRESH } from "modules/patient/sections/chiefComplaints/events";
import { DX_V2_REFRESH } from "modules/patient/sections/diagnosis/events";
import { MED_V2_REFRESH } from "modules/patient/sections/medicines/events";
import { LI_V2_REFRESH } from "modules/patient/sections/labInvestigations/events";
import DigitiseLauncher from "modules/patient/sections/labInvestigations/DigitiseLauncher";

import MOBILE from "../mobileConfig";
import useIsMobile from "../useIsMobile";

/**
 * Sits on the Create Rx route so the screen can be REMOUNTED after a record is
 * saved from inside a bottom sheet.
 *
 * WHY A REMOUNT, AND WHY IT HAS TO LIVE OUT HERE.
 *
 * Adding a complaint on mobile happens in a sheet on this screen rather than by
 * going to the patient record and back. The form saves through its own service
 * and announces it on the section's own refresh channel — but the Rx screen has
 * no listener and no refetch for its picker lists (its seed effect is keyed on
 * the patient and the appointment, and nothing else re-enters it). Something
 * has to make it read the lists again.
 *
 * Re-seeding in place is the obvious move and it is WRONG: the seed's tail runs
 * `setPicks(restored ? mergePicks(...) : base)`, which replaces the ticks with
 * the freshly derived "new today" set — every box the doctor ticked by hand
 * would quietly come undone.
 *
 * A remount is the path the product already uses. The unmount effect parks the
 * builder in sessionStorage, the next mount reads it back and merges the picks:
 * exactly the round trip the DESKTOP detour relies on when it navigates to the
 * patient record and returns. Taking the navigation out leaves the round trip
 * intact — and a component cannot remount itself, so the key belongs to a
 * parent, which is this.
 *
 * On desktop, and with the flag off, this renders ReviewPrescription with no
 * key and no listeners — identical to what the route did before.
 */
const REFRESH_CHANNELS = [CC_V2_REFRESH, DX_V2_REFRESH, MED_V2_REFRESH, LI_V2_REFRESH];

const MobilePrescriptionRoute = (props) => {
  const mobile = useIsMobile() && MOBILE.prescriptionScreen;
  const [rev, setRev] = useState(0);

  useEffect(() => {
    if (!mobile) return undefined;
    const bump = () => setRev((r) => r + 1);
    REFRESH_CHANNELS.forEach((c) => EventService.addListener(c, bump));
    return () => REFRESH_CHANNELS.forEach((c) => EventService.removeListener(c, bump));
  }, [mobile]);

  /* The key only ever changes on mobile, so desktop never remounts.

     DigitiseLauncher sits BESIDE the keyed screen, not inside it, and that is
     the whole reason it is mounted here. "Digitise now" emits LI_V2_REFRESH the
     moment the trigger succeeds (DigitiseModal.js:123-126), which bumps `rev`
     and remounts the screen — so a modal hosted inside it would vanish before
     its "digitised successfully" stage was ever seen. Out here it survives the
     remount. Not mounted on desktop, where "+ Add lab investigation" still
     takes the `&add=1` trip to the patient record. */
  return (
    <>
      <ReviewPrescription key={mobile ? rev : undefined} {...props} />
      {mobile && MOBILE.labAddOpensDigitise ? <DigitiseLauncher /> : null}
    </>
  );
};

export default MobilePrescriptionRoute;
